<template>
  <div class="flex max-w-[384px] flex-col gap-4">
    <div v-if="dialogPhase === DialogPhase.Conversation && isExpanded" class="flex flex-col">
      <template v-if="conversationNoteGroups">
        <h4
          v-if="conversationNoteGroups.length > 0"
          class="text-title-small text-on-surface-variant mb-1"
        >
          {{ $t('comment.conversation-note.title') }}
        </h4>
        <div class="flex flex-col gap-2">
          <div
            v-for="(commentGroup, groupIndex) in conversationNoteGroups"
            :key="groupIndex"
            class="flex flex-col gap-0.5"
          >
            <CommentItem
              v-for="(comment, commentIndex) in commentGroup"
              :key="comment.id"
              :comment="comment"
              :is-first="commentIndex == 0"
              :is-editable="canEditComments"
              @update:comment="emit('update:comment', $event)"
              @delete:comment="emit('delete:comment', comment.id)"
            />
          </div>
        </div>
      </template>
      <BaseTextButton
        v-if="canCreateComments && !showForm && conversationNoteGroups.length === 0"
        icon="add_notes"
        color="filledTertiary"
        class="mt-1 w-full"
        @click="emit('create:comment', true)"
      >
        {{ $t('comment.create.conversation-note') }}
      </BaseTextButton>
      <BaseIconButton
        v-else-if="canCreateComments && !showForm && conversationNoteGroups.length > 0"
        v-tooltip="$t('comment.create.conversation-note')"
        icon="add"
        size="XS"
        shape="square"
        color="outlined"
        class="mt-1 w-full"
        @click="emit('create:comment', true)"
      />
    </div>
    <div
      v-if="isExpanded && dialogPhase === DialogPhase.Conversation && commentGroups.length > 0"
      class="border-outline-variant border-t"
    ></div>
    <div class="flex flex-col">
      <template v-if="commentGroups">
        <h4 v-if="commentGroups.length > 0" class="text-title-small text-on-surface-variant mb-1">
          {{ $t('comment.title.individual') }}
        </h4>
        <div class="flex flex-col gap-2">
          <div
            v-for="(commentGroup, groupIndex) in commentGroups"
            :key="groupIndex"
            class="flex flex-col gap-0.5"
          >
            <CommentItem
              v-for="(comment, commentIndex) in commentGroup"
              :key="comment.id"
              :comment="comment"
              :is-first="commentIndex == 0"
              :is-editable="canEditComments"
              :can-promote-to-conversation-note="canPromoteToConversationNote"
              @update:comment="emit('update:comment', $event)"
              @delete:comment="emit('delete:comment', comment.id)"
            />
          </div>
        </div>
      </template>
      <template
        v-if="
          canCreateComments && !showForm && isExpanded && dialogPhase !== DialogPhase.Conversation
        "
      >
        <BaseTextButton
          v-if="commentGroups.length === 0"
          icon="add_comment"
          color="filled"
          class="mt-1 w-full"
          @click="emit('create:comment', false)"
        >
          {{ $t('comment.create.comment') }}
        </BaseTextButton>
        <BaseIconButton
          v-else
          v-tooltip="$t('comment.create.comment')"
          icon="add"
          size="XS"
          shape="square"
          color="outlined"
          class="mt-1 w-full"
          @click="emit('create:comment', false)"
        />
      </template>
    </div>

    <BaseIconButton
      v-if="
        canCreateComments && !showForm && !isExpanded && dialogPhase !== DialogPhase.Conversation
      "
      v-tooltip="$t('comment.create.comment')"
      icon="add_comment"
      size="M"
      color="filled"
      @click="emit('create:comment', false)"
    />
    <BaseIconButton
      v-if="
        canCreateComments && !showForm && !isExpanded && dialogPhase === DialogPhase.Conversation
      "
      v-tooltip="$t('comment.create.conversation-note')"
      icon="add_notes"
      size="M"
      color="filledTertiary"
      @click="emit('create:comment', true)"
    />

    <BaseDialog
      type="basic"
      :title="createDialogTitle"
      :is-open="showForm"
      class="md:min-w-[500px]"
      @close="emit('cancel:create')"
    >
      <WysiwygTextField v-model="creatableComment.text" autofocus hide-headlines />
      <template #dismiss>
        <BaseTextButton color="text" @click="emit('cancel:create')">{{
          $t('common.cancel')
        }}</BaseTextButton>
      </template>
      <template #confirm>
        <BaseTextButton color="filled" @click="emit('save:comment')">
          {{ $t('common.save') }}
        </BaseTextButton>
      </template>
    </BaseDialog>
  </div>
</template>
<script lang="ts" setup>
  import { computed } from 'vue'
  import { useI18n } from 'vue-i18n'
  import type { Comment, CreateableComment } from '@/core/composables/use-comments'
  import BaseTextButton from '../../../core/components/base/button/text-button/BaseTextButton.vue'
  import WysiwygTextField from '../../../core/components/base/wysiwyg/WysiwygTextField.vue'
  import BaseIconButton from '../base/button/icon-button/BaseIconButton.vue'
  import BaseDialog from '../base/dialog/BaseDialog.vue'
  import CommentItem from './CommentItem.vue'
  import { DialogPhase } from '@/core/models/dialog/dialog.ts'

  interface Props {
    canCreateComments?: boolean
    canEditComments?: boolean
    canPromoteToConversationNote?: boolean
    commentGroups: Comment[][]
    showForm: boolean
    creatableComment: Partial<CreateableComment>
    isExpanded?: boolean
    dialogPhase: DialogPhase
    conversationNoteGroups: Comment[][]
  }

  const props = defineProps<Props>()

  const emit = defineEmits<{
    (e: 'update:comment', comment: Comment): void
    (e: 'delete:comment', commentId: string): void
    (e: 'create:comment', isConversationNote: boolean): void
    (e: 'cancel:create'): void
    (e: 'save:comment'): void
    (e: 'update:creatableCommentText', text: string): void
  }>()

  const { t } = useI18n()

  const createDialogTitle = computed(() =>
    props.creatableComment.isConversationNote
      ? t('comment.create.conversation-note')
      : t('comment.create.comment'),
  )
</script>
